import React from 'react';
import PropTypes from 'prop-types';
import { IntlProvider, useIntl } from 'react-intl';
import Translation from '../../../../../components/stateless/Translation';
import { RENT_MONTH_NAMES, VALUE_COVERAGE_MONTH_NAMES } from '../constants';
import Price from '../../../../../components/stateless/Price';
import SMTooltip from '../../../../components/stateless/SMTooltip';

const propTypes = {
  lineItem: PropTypes.shapeOf().isRequired,
  currency: PropTypes.string().isRequired,
  currencyLocale: PropTypes.string().isRequired,
  discountPlans: PropTypes.array().isRequired,
};
const defaultProps = {
};

const dateRegex = /[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{4}/;
const valueCoverageRegex = /protection plan/i;
const rentItemRegex = /rent unit/i;
const webOnlyText = '[WEB ONLY] ';
const adminFeeRegex = /administrative fee/i;
const lockRegex = /lock/i;

const RentLineItem = ({
  children,
  currency,
  currencyLocale,
  price,
}) => (
  <div className="flex gap-2 place-content-between mb-2">
    <div>
      {children}
    </div>
    <Price
      currency={currency}
      locale={currencyLocale}
      value={price}
    />
  </div>
);

const ValueCoverageLineItem = ({
  children,
  currency,
  currencyLocale,
  price,
}) => (
  <div className="flex gap-2 place-content-between mb-2" data-value-coverage-line-item="true">
    <div>
      {children}
    </div>
    <Price
      currency={currency}
      locale={currencyLocale}
      value={price}
    />
  </div>
);


const DiscountLineItem = ({
  children,
  currency,
  currencyLocale,
  price,
  discountPlanName,
}) => (
  <div className="flex gap-2 place-content-between mb-2">
    <div className="border-l-1 pl-2 ml-1 border-black">
      {children}
    </div>
    <div className="text-[#2a8703]">
      {price === 0 ? (
        <Translation id={`widgets.unitstablev2.storageMart.unitPromo.${discountPlanName}.lineItemAmount`} defaultMessage="Applied Next Month" />
      ) : (
        <Price
          currency={currency}
          locale={currencyLocale}
          value={price}
        />
      )}
    </div>
  </div>
);

const AdminFeeLineItem = ({
  currency,
  currencyLocale,
  price,
}) => (
  <div className="flex gap-2 place-content-between mb-2">
    <div>
      <Translation
        id="widgets.checkout.summary.adminonetimefee"
        defaultMessage="One-time Admin Fee"
      />
    </div>
    <Price
      currency={currency}
      locale={currencyLocale}
      value={price}
    />
  </div>
);

const LockLineItem = ({
  currency,
  currencyLocale,
  price,
  locale,
  messages,
}) => (
  <div className="flex gap-2 place-content-between mb-2">
    <div className="flex gap-2">
      <Translation
        id="widgets.checkout.summary.merchandise"
        defaultMessage="Lock Fee"
      />
      <SMTooltip
        body={(
          <IntlProvider locale={locale} messages={messages}>
            <Translation
              id="widgets.checkout.summary.lockDisclaimer"
              defaultMessage="You may return the unused lock"
            />
          </IntlProvider>
        )}
      />
    </div>
    <Price
      currency={currency}
      locale={currencyLocale}
      value={price}
    />
  </div>
);

const LineItem = ({
  children,
  currency,
  currencyLocale,
  price,
}) => (
  <div className="flex gap-2 place-content-between mb-2">
    <div>
      {children}
    </div>
    <Price
      currency={currency}
      locale={currencyLocale}
      value={price}
    />
  </div>
);

const SummaryLineItem = ({
  lineItem,
  currency,
  currencyLocale,
  discountPlans,
}) => {
  const { formatMessage, locale, messages } = useIntl();
  let lineItemsToRender = [];
  const fullDescription = lineItem.fullDescription || lineItem.full_description;
  let lineItemPrice = lineItem.amount >= 0 ? lineItem.amount : lineItem.subtotal;
  const discountAmount = lineItem.discountAmount !== undefined
    ? lineItem.discountAmount
    : lineItem.discount_amount;
  const dateResult = dateRegex.exec(fullDescription);
  const valueCoverageResult = valueCoverageRegex.exec(fullDescription);
  const rentItemResult = rentItemRegex.exec(fullDescription);
  let mainDescription = lineItem.description.replace(webOnlyText, '');

  if (dateResult) {
    const [month] = dateResult[0].split('/');
    mainDescription = valueCoverageResult
      ? formatMessage(VALUE_COVERAGE_MONTH_NAMES[month])
      : formatMessage(RENT_MONTH_NAMES[month]);
  }

  if (rentItemResult && discountPlans?.length) {
    lineItemPrice += discountAmount;
    lineItemsToRender = [
      (
        <div className="border-t-1 border-b-1 pt-2 pb-2 mb-2">
          <div className="mb-1">
            <Translation
              id="widgets.checkout.summary.discountsTitle"
              defaultMessage="Discounts"
            />
          </div>
          {discountPlans.map(discountPlan => (
            <DiscountLineItem
              currency={currency}
              currencyLocale={currencyLocale}
              price={discountPlan.amount * -1}
              discountPlanName={discountPlan.name}
            >
              <Translation id={`widgets.unitstablev2.storageMart.unitPromo.${discountPlan.name}`} />
            </DiscountLineItem>
          ))}
        </div>
      ),
    ];
  }

  if (rentItemResult) {
    lineItemsToRender.unshift((
      <>
        <RentLineItem currency={currency} currencyLocale={currencyLocale} price={lineItemPrice}>
          {mainDescription}
        </RentLineItem>
        {discountPlans.map(discountPlan => (
          <div className="mt-2 mb-2 flex gap-2">
            <div><i className="fa fa-tag text-[#007a7c]" aria-hidden="true" /></div>
            <div className="text-[#565958]">
              <Translation
                id={`widgets.checkout.storedge.summary.discount.${discountPlan.name}`}
                defaultMessage="You are receiving a discount on your unit."
              />
            </div>
          </div>
        ))}
      </>
    ));
  }

  if (valueCoverageResult) {
    lineItemsToRender.unshift((
      <ValueCoverageLineItem
        currency={currency}
        currencyLocale={currencyLocale}
        price={lineItemPrice}
      >
        {mainDescription}
      </ValueCoverageLineItem>
    ));
  }

  if (adminFeeRegex.exec(fullDescription)) {
    lineItemsToRender.unshift((
      <AdminFeeLineItem
        currency={currency}
        currencyLocale={currencyLocale}
        price={lineItemPrice}
      >
        {mainDescription}
      </AdminFeeLineItem>
    ));
  }

  if (lockRegex.exec(fullDescription)) {
    lineItemsToRender.unshift((
      <LockLineItem
        currency={currency}
        currencyLocale={currencyLocale}
        price={lineItemPrice}
        locale={locale}
        messages={messages}
      >
        {mainDescription}
      </LockLineItem>
    ));
  }

  if (!lineItemsToRender.length) {
    lineItemsToRender.unshift((
      <LineItem
        currency={currency}
        currencyLocale={currencyLocale}
        price={lineItemPrice}
      >
        {mainDescription}
        {lineItem.quantity > 1 ? ` (${lineItem.quantity})` : null}
      </LineItem>
    ));
  }

  return (
    <>
      {lineItemsToRender.map(item => (
        item
      ))}
    </>
  );
};

SummaryLineItem.propTypes = propTypes;
SummaryLineItem.defaultProps = defaultProps;

export default SummaryLineItem;
